// Medication adherence section (step 4) for VisitReportPage.

import { useRef, type JSX } from "react";
import { FormField } from "./FormField.js";
import { SelectField } from "./SelectField.js";
import type { FormState, Action } from "../lib/visit-report-types.js";
import { SECTION, HEADING } from "../lib/visit-report-types.js";
import type { SectionAutoSaveBus } from "./VisitReportWizard.js";
import { fieldErrorMessage, type SectionFieldErrors } from "../lib/visit-report-section-validators.js";
import { useFocusOnApiField } from "../hooks/use-focus-on-api-field.js";

const ADHERENCE_OPTS = [
  { value: "compliant_all_doses",       label: "Taken (all doses)" },
  { value: "missed_one_dose",           label: "Missed one dose" },
  { value: "missed_multiple_doses",     label: "Missed multiple doses" },
  { value: "patient_refused",           label: "Patient refused" },
  { value: "not_available_pharmacy",    label: "Not available / pharmacy" },
  { value: "unable_to_assess",          label: "Unable to assess" },
];

interface MedicationProps {
  f: FormState; dispatch: (a: Action) => void;
  bus?: SectionAutoSaveBus; fieldErrors?: SectionFieldErrors;
  /** Q9 S7 — when set, the named field is focused after this section renders. */
  focusFieldName?: string | null;
}

export function MedicationSection({ f, dispatch, bus, fieldErrors, focusFieldName }: MedicationProps): JSX.Element {
  const update = (i: number, key: "drug_name" | "adherence_status", v: string): void => {
    const next = f.meds.map((m, idx) => idx === i ? { ...m, [key]: v } : m);
    dispatch({ field: "meds", value: next });
  };
  // drug_name is free-text → blur fires an immediate save like a textarea.
  const drugNameProps = { onBlur: bus?.onFieldBlur };
  // Per-row error keys come back as `medication_adherence_log.<i>.<sub>` from
  // server validation; client-side validator emits the same paths (D8 8a).
  const rowErr = (i: number, sub: "drug_name" | "adherence_status"): string | undefined =>
    fieldErrorMessage(fieldErrors, `medication_adherence_log.${i}.${sub}`);
  const containerRef = useRef<HTMLDivElement>(null);
  useFocusOnApiField(containerRef, focusFieldName);
  return (
    <div ref={containerRef} className={SECTION}>
      <p className={HEADING}>4 — Medication Adherence</p>
      <div className="space-y-2">
        {f.meds.map((m, i) => (
          <div key={i} className="grid grid-cols-2 gap-2">
            <FormField label={`Medication ${i + 1}`} value={m.drug_name}
              onChange={(v) => update(i, "drug_name", v)} placeholder="Drug name"
              error={rowErr(i, "drug_name")} apiFieldName={`medication_adherence_log.${i}.drug_name`} {...drugNameProps} />
            <SelectField label="Adherence" value={m.adherence_status}
              onChange={(v) => update(i, "adherence_status", v)} options={ADHERENCE_OPTS}
              error={rowErr(i, "adherence_status")} apiFieldName={`medication_adherence_log.${i}.adherence_status`} />
          </div>
        ))}
      </div>
      {f.meds.length < 5 && (
        <button type="button"
          onClick={() => dispatch({ field: "meds", value: [...f.meds, { drug_name: "", adherence_status: "compliant_all_doses" }] })}
          className="mt-2 text-sm text-navy-700 hover:underline">
          + Add medication
        </button>
      )}
      <label className="flex items-start gap-2 mt-4 cursor-pointer">
        <input type="checkbox" checked={f.medication_list_verified}
          onChange={(e) => dispatch({ field: "medication_list_verified", value: e.target.checked })}
          className="mt-0.5 rounded border-slate-300 text-navy-700 focus:ring-navy-500" />
        <span className="text-sm text-slate-700">
          I have verified the medication list is current and complete
          <span className="block text-xs text-slate-400 mt-0.5">Required in Nsofar compliance mode</span>
        </span>
      </label>
    </div>
  );
}
